/**
 * The shell for the PLATFORM levels — the Super Admin console and the
 * portal-admin screens. A fixed rail on the left carrying identity, nav and
 * the account; below `lg:` it collapses to a dark top bar with a hamburger and
 * the same nav slides in as a drawer, so nothing is cut off on a phone.
 *
 * Restyled 26 Aug 2026 (owner's direction) into the public home page's
 * language — see the header of `components/consoleUi.jsx` for the full set of
 * rules. What changed here, and why:
 *
 *   - The rail carries the REAL logo now (`RunnerCrewMark`, the same yellow
 *     square the marketing site and every entry screen draw) instead of a
 *     text-only "RUNNER·CREW" that existed nowhere else in the product.
 *   - Its surface is the home hero's ink gradient rather than a flat black, so
 *     the rail reads as the same material as the landing page.
 *   - The separator is the gold→teal hairline from the home page's scroll
 *     indicator. The dashed amber lane marking belongs to the CLUB app — this
 *     is the platform, and the two should not wear the same badge.
 *   - Nav items are square with a gold marker on the active row. The old
 *     rounded pill put a 10px radius next to a design that has none.
 *
 * Identity: the platform is RunnerCrew; a portal shell shows the portal's own
 * name with the platform mark still beside it. Club names never appear here —
 * those belong to the club shell.
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DemoBanner } from './DemoBanner';
import { useBackToClose } from '../hooks/useBackToClose';
import { Icon, RunnerCrewMark } from './ui';

/** The rail's ink surface — the home hero's gradient, turned vertical. */
const RAIL_SURFACE = 'bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_52%,#0A0E19_100%)]';

/** The gold→teal hairline that separates identity from nav. */
function AccentRule({ className = '' }) {
  return (
    <div className={`h-[2px] flex-none bg-[linear-gradient(90deg,#F6C019,#7FD8C8)] ${className}`} />
  );
}

function PlatformMark({ title, subtitle }) {
  return (
    <div className="flex items-center gap-2.5">
      <RunnerCrewMark size={30} className="flex-none" />
      <div className="min-w-0 leading-none">
        <div className="truncate font-market text-[15.5px] font-extrabold tracking-[-0.02em] text-white">
          {title ?? 'RunnerCrew'}
        </div>
        <div className="mt-[3px] font-market text-[8.5px] font-bold uppercase tracking-[0.28em] text-gold-500/90">
          {subtitle}
        </div>
      </div>
    </div>
  );
}

export function ConsoleShell({ title, subtitle, tabs, children }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  /* A drawer that survives navigation would cover the page you just asked
     for. Close it whenever the route changes. */
  useEffect(() => setNavOpen(false), [location.pathname]);
  // The phone's Back closes the menu instead of leaving the page under it.
  useBackToClose(navOpen, () => setNavOpen(false));

  function SwitchButton({ className = '' }) {
    return (
      <button
        type="button"
        onClick={() => navigate('/select-club')}
        className={`mt-3.5 inline-flex items-center gap-1.5 border border-white/[.18] bg-white/[.05] px-2.5 py-[5px] font-market text-[9.5px] font-bold uppercase tracking-[0.18em] text-white/60 transition-colors duration-200 hover:border-gold-500/60 hover:bg-gold-500/[.08] hover:text-gold-500 ${className}`}
      >
        ⇄ Switch
      </button>
    );
  }

  function NavItem({ to, label, icon }) {
    return (
      <NavLink
        to={to}
        end={to.split('/').length <= 3}
        onClick={() => window.scrollTo(0, 0)}
        className={({ isActive }) =>
          /* `relative` carries the active marker, which is an absolutely
             positioned gold bar rather than a border — a border would shift
             the label by 3px every time the row became active. */
          `relative flex items-center gap-3 py-[13px] pl-5 pr-3.5 font-market text-[13.5px] font-bold tracking-[-0.005em] transition-colors duration-200 ${
            isActive
              ? 'bg-white/[.07] text-white'
              : 'text-white/55 hover:bg-white/[.035] hover:text-white'
          }`
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span className="absolute inset-y-0 left-0 w-[3px] bg-gold-500" aria-hidden="true" />
            )}
            <Icon
              name={icon}
              size={16}
              className={`flex-none ${isActive ? 'text-gold-500' : 'text-white/35'}`}
            />
            <span className="flex-1">{label}</span>
          </>
        )}
      </NavLink>
    );
  }

  /** The signed-in account, at the foot of the rail and of the drawer. */
  function AccountBlock() {
    return (
      <div className="border-t border-white/[.09] px-5 py-4">
        <div className="flex items-center gap-2.5">
          {/* Square, like the logo mark. A lone circle in a design with no
              other radius is the thing your eye catches first. */}
          <span className="grid h-9 w-9 flex-none place-items-center bg-gold-500 font-market text-[12px] font-extrabold text-ink-800">
            {(user?.name ?? 'RC')
              .split(' ')
              .map((w) => w[0])
              .slice(0, 2)
              .join('')
              .toUpperCase()}
          </span>
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block truncate font-market text-[13px] font-bold text-white">
              {user?.name ?? '—'}
            </span>
            <span className="block font-market text-[9px] font-bold uppercase tracking-[0.2em] text-white/40">
              {subtitle}
            </span>
          </span>
          <button
            type="button"
            className="flex-none p-2 text-white/45 transition-colors duration-200 hover:bg-white/[.07] hover:text-gold-500"
            onClick={() => {
              signOut();
              navigate('/signin', { replace: true });
              window.scrollTo(0, 0);
            }}
            title="Sign out"
          >
            <Icon name="signout" size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ---------- the rail: desktop only ---------- */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 hidden w-[248px] flex-col border-r border-white/[.06] lg:flex ${RAIL_SURFACE}`}
      >
        <div className="px-5 pb-5 pt-7">
          <PlatformMark title={title} subtitle={subtitle} />
          <SwitchButton />
        </div>
        <AccentRule className="mx-5" />

        <nav className="flex-1 overflow-y-auto py-4">
          {tabs.map(([to, label, icon]) => (
            <NavItem key={to} to={to} label={label} icon={icon} />
          ))}
        </nav>

        <AccountBlock />
      </aside>

      {/* ---------- the drawer: phone only ---------- */}
      {navOpen && (
        <div
          className="fixed inset-0 z-[60] bg-ink-900/70 backdrop-blur-sm lg:hidden"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setNavOpen(false);
          }}
          role="presentation"
        >
          <aside
            className={`flex h-full w-[272px] max-w-[82vw] flex-col motion-safe:animate-slidein ${RAIL_SURFACE}`}
            role="dialog"
            aria-label="Menu"
          >
            <div className="flex items-start justify-between px-5 pb-4 pt-6">
              <div className="min-w-0">
                <PlatformMark title={title} subtitle={subtitle} />
                <SwitchButton />
              </div>
              <button
                type="button"
                className="-mr-1 flex-none p-1.5 text-white/50 transition-colors duration-200 hover:bg-white/[.07] hover:text-gold-500"
                onClick={() => setNavOpen(false)}
                title="Close menu"
              >
                <Icon name="close" size={17} strokeWidth={2.2} />
              </button>
            </div>
            <AccentRule className="mx-5" />

            <nav className="flex-1 overflow-y-auto py-3">
              {tabs.map(([to, label, icon]) => (
                <NavItem key={to} to={to} label={label} icon={icon} />
              ))}
            </nav>

            <AccountBlock />
          </aside>
        </div>
      )}

      {/* ---------- the content column ---------- */}
      <div className={`flex min-h-screen flex-col bg-ground lg:pl-[248px]`}>
        <header className={`sticky top-0 z-30 lg:hidden ${RAIL_SURFACE}`}>
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              type="button"
              className="-ml-1.5 p-1.5 text-white/70 transition-colors duration-200 hover:bg-white/[.07] hover:text-gold-500"
              onClick={() => setNavOpen(true)}
              title="Menu"
            >
              <Icon name="menu" size={20} strokeWidth={2.2} />
            </button>

            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2.5 text-left leading-none"
              onClick={() => navigate('/select-club')}
              title="Switch"
            >
              <RunnerCrewMark size={26} className="flex-none" />
              <span className="min-w-0">
                <span className="block truncate font-market text-[14px] font-extrabold tracking-[-0.02em] text-white">
                  {title ?? 'RunnerCrew'}
                </span>
                <span className="mt-[3px] block font-market text-[8px] font-bold uppercase tracking-[0.22em] text-gold-500/90">
                  {subtitle} · tap to switch
                </span>
              </span>
            </button>

            <button
              type="button"
              className="flex-none p-2 text-white/70 transition-colors duration-200 hover:bg-white/[.07] hover:text-gold-500"
              onClick={() => {
                signOut();
                navigate('/signin', { replace: true });
              }}
              title="Sign out"
            >
              <Icon name="signout" size={16} strokeWidth={2} />
            </button>
          </div>
          <AccentRule />
        </header>

        <DemoBanner />

        <main className="mx-auto w-full max-w-[1180px] flex-1 px-4 pb-16 pt-5 sm:px-8 sm:pt-8">
          {children ?? <Outlet />}
        </main>
      </div>
    </>
  );
}
